import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { BrregOrgDto, useLookupBrregOrgQuery, useSearchBrregOrgsByNameQuery } from "../../autogen/bff-api";
import { isApiErrorDto } from "../errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../errors/isFetchBaseQueryError";
import { useBaseData } from "../useBaseData";

const MIN_NAME_LENGTH = 2;
const RESULT_LIMIT = 5;
const DEBOUNCE_MS = 300;
const ORG_NUMBER_LENGTH = 9;
const BRREG_UNAVAILABLE_ERROR_CODE = "zz_OrganizationApiErrorCode_BrregUnavailable";

export type BrregSearchMode = "idle" | "name" | "orgNumber";

interface Args {
  countryId: string | undefined;
  query: string | undefined;
  enabled?: boolean;
}

export interface BrregSearchResult {
  isNorwaySelected: boolean;
  matches: BrregOrgDto[];
  isFetching: boolean;
  isUnavailable: boolean;
  invalidOrgNumber: boolean;
  mode: BrregSearchMode;
}

export function useBrregSearch({ countryId, query = "", enabled = true }: Args): BrregSearchResult {
  const baseData = useBaseData();
  const norwayCountryId = baseData.countries.find((c) => c.alpha3Code === "NOR")?.id;
  const isNorwaySelected = !!norwayCountryId && countryId === norwayCountryId;

  const [debouncedQuery, setDebouncedQuery] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDebounced = useCallback(
    debounce((value: string) => setDebouncedQuery(value), DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    setDebounced(query.trim());
  }, [query, setDebounced]);

  const trimmed = debouncedQuery.trim();
  const compact = trimmed.replace(/\s/g, "");
  const isNumeric = compact.length > 0 && /^\d+$/.test(compact);
  const isCompleteOrgNumber = isNumeric && compact.length === ORG_NUMBER_LENGTH;
  const invalidOrgNumber = isNumeric && compact.length !== ORG_NUMBER_LENGTH;

  const active = enabled && isNorwaySelected;
  const mode: BrregSearchMode = !active
    ? "idle"
    : isNumeric
      ? "orgNumber"
      : trimmed.length >= MIN_NAME_LENGTH
        ? "name"
        : "idle";

  const lookup = useLookupBrregOrgQuery({ organizationNumber: compact }, { skip: !active || !isCompleteOrgNumber });
  const nameSearch = useSearchBrregOrgsByNameQuery({ query: trimmed, limit: RESULT_LIMIT }, { skip: mode !== "name" });

  const isUnavailable = isBrregUnavailable(lookup.error) || isBrregUnavailable(nameSearch.error);

  const matches: BrregOrgDto[] =
    mode === "orgNumber"
      ? lookup.data
        ? [lookup.data]
        : []
      : mode === "name"
        ? nameSearch.data?.orgs ?? []
        : [];

  return {
    isNorwaySelected,
    matches,
    isFetching: (mode === "orgNumber" && lookup.isFetching) || (mode === "name" && nameSearch.isFetching),
    isUnavailable,
    invalidOrgNumber,
    mode,
  };
}

function isBrregUnavailable(error: unknown): boolean {
  return (
    !!error &&
    isFetchBaseQueryError(error) &&
    isApiErrorDto(error.data) &&
    error.data.errorCode === BRREG_UNAVAILABLE_ERROR_CODE
  );
}
